/**
 * LinkedIn Ads results that arrive on their own.
 *
 * The CMS asks LinkedIn once a week for the last four weeks of numbers (views, clicks, sign-ups, spend) and saves them as
 * results. This file holds the pure parts: the addresses to ask, how each answer is read, and how days become Monday weeks.
 * Nothing here talks to LinkedIn or the database. Read only: nothing can change an ad account.
 *
 * Same rules as the rest of Ads: an unknown number stays null (never 0), a campaign that is not SonicHire or Raya is
 * skipped and counted, and an answer that cannot be read is reported, not guessed.
 */
import { z } from "zod";
import { adResultKey, isValidIsoDate } from "./ads.ts";
import { productFromCampaign, weekEnd } from "./adsGoogle.ts";
import type { AdProductId } from "./adsProducts.ts";

export const LINKEDIN_API_VERSION = "202510";
export const LINKEDIN_API_BASE = "https://api.linkedin.com";
export const LINKEDIN_TOKEN_URL = "https://www.linkedin.com/oauth/v2/accessToken";
/** How far back each weekly pull looks, so a missed week fills itself in and the current week keeps growing. */
export const LINKEDIN_LOOKBACK_DAYS = 28;

export const LINKEDIN_SECRET_NAMES = ["LINKEDIN_ADS_CLIENT_ID", "LINKEDIN_ADS_CLIENT_SECRET", "LINKEDIN_ADS_REFRESH_TOKEN", "LINKEDIN_ADS_ACCOUNT_ID"] as const;

export const linkedinAccountIdSchema = z.string().trim().regex(/^\d{3,15}$/, "The ad account number is digits only, for example 518012345");

const ymd = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `(year:${y},month:${m},day:${d})`;
};

export function linkedinAccountUrl(accountId: string): string {
  return `${LINKEDIN_API_BASE}/rest/adAccounts/${accountId}`;
}

export function linkedinCampaignsUrl(accountId: string): string {
  return `${LINKEDIN_API_BASE}/rest/adAccounts/${accountId}/adCampaigns?q=search&pageSize=100`;
}

export function linkedinAnalyticsUrl(accountId: string, from: string, to: string): string {
  const fields = "impressions,clicks,costInLocalCurrency,externalWebsiteConversions,dateRange,pivotValues";
  return `${LINKEDIN_API_BASE}/rest/adAnalytics?q=analytics&pivot=CAMPAIGN&timeGranularity=DAILY&dateRange=(start:${ymd(from)},end:${ymd(to)})&accounts=List(urn%3Ali%3AsponsoredAccount%3A${accountId})&fields=${fields}`;
}

export function linkedinHeaders(accessToken: string): Record<string, string> {
  return { Authorization: `Bearer ${accessToken}`, "LinkedIn-Version": LINKEDIN_API_VERSION, "X-Restli-Protocol-Version": "2.0.0" };
}

/** The Monday of the week a day falls in. */
export function mondayOf(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

const dayParts = z.object({ year: z.number().int(), month: z.number().int(), day: z.number().int() });
const nullableNumber = z.union([z.number(), z.string()]).nullish().transform((v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
});

const analyticsElementSchema = z.object({
  impressions: nullableNumber,
  clicks: nullableNumber,
  costInLocalCurrency: nullableNumber,
  externalWebsiteConversions: nullableNumber,
  dateRange: z.object({ start: dayParts }),
  pivotValues: z.array(z.string()).min(1),
});

export const linkedinAnalyticsSchema = z.object({ elements: z.array(z.unknown()).default([]) });
export const linkedinCampaignsSchema = z.object({ elements: z.array(z.object({ id: z.union([z.number(), z.string()]), name: z.string() })).default([]) });

export interface LinkedinWeekRow {
  product: AdProductId;
  periodStart: string;
  periodEnd: string;
  campaign: string;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  spend: number | null;
  currency: string;
  rowKey: string;
}

export interface LinkedinRead {
  weeks: LinkedinWeekRow[];
  /** Campaigns that are not SonicHire or Raya, so nothing is silently dropped. */
  skippedCampaigns: string[];
  /** Day rows LinkedIn sent that could not be read. */
  unreadable: number;
}

const add = (a: number | null, b: number | null) => (b === null ? a : (a ?? 0) + b);

/**
 * Turns LinkedIn's daily rows into one row per campaign per Monday week. A number LinkedIn did not send stays unknown;
 * it is never counted as zero.
 */
export function readLinkedinAnalytics(analytics: unknown, campaigns: unknown, currency: string): LinkedinRead {
  const a = linkedinAnalyticsSchema.safeParse(analytics);
  const c = linkedinCampaignsSchema.safeParse(campaigns);
  const names = new Map<string, string>();
  if (c.success) for (const e of c.data.elements) names.set(String(e.id), e.name.trim());

  const acc = new Map<string, { product: AdProductId; periodStart: string; campaign: string; impressions: number | null; clicks: number | null; conversions: number | null; spend: number | null }>();
  const skipped = new Set<string>();
  let unreadable = a.success ? 0 : 1;

  for (const raw of a.success ? a.data.elements : []) {
    const p = analyticsElementSchema.safeParse(raw);
    if (!p.success) {
      unreadable += 1;
      continue;
    }
    const e = p.data;
    const id = e.pivotValues[0]!.split(":").pop() ?? "";
    const name = names.get(id);
    if (!name) {
      skipped.add(`Campaign ${id}`);
      continue;
    }
    const product = productFromCampaign(name);
    if (!product) {
      skipped.add(name);
      continue;
    }
    const day = `${e.dateRange.start.year}-${String(e.dateRange.start.month).padStart(2, "0")}-${String(e.dateRange.start.day).padStart(2, "0")}`;
    if (!isValidIsoDate(day)) {
      unreadable += 1;
      continue;
    }
    if (e.impressions !== null && e.clicks !== null && e.clicks > e.impressions) {
      unreadable += 1;
      continue;
    }
    const periodStart = mondayOf(day);
    const k = `${id}|${periodStart}`;
    const cur = acc.get(k) ?? { product, periodStart, campaign: name, impressions: null, clicks: null, conversions: null, spend: null };
    cur.impressions = add(cur.impressions, e.impressions);
    cur.clicks = add(cur.clicks, e.clicks);
    cur.conversions = add(cur.conversions, e.externalWebsiteConversions === null ? null : Math.round(e.externalWebsiteConversions));
    cur.spend = add(cur.spend, e.costInLocalCurrency);
    acc.set(k, cur);
  }

  const weeks = [...acc.values()]
    .sort((x, y) => (x.periodStart === y.periodStart ? x.campaign.localeCompare(y.campaign) : x.periodStart.localeCompare(y.periodStart)))
    .map((w): LinkedinWeekRow => {
      const periodEnd = weekEnd(w.periodStart);
      return { ...w, spend: w.spend === null ? null : Math.round(w.spend * 100) / 100, periodEnd, currency, rowKey: adResultKey({ product: w.product, channel: "linkedin", periodStart: w.periodStart, periodEnd, campaign: w.campaign, adName: null }) };
    });
  return { weeks, skippedCampaigns: [...skipped].sort(), unreadable };
}

/** The sentence shown in the run history. */
export function linkedinRunLine(r: LinkedinRead): string {
  const parts = [`Received ${r.weeks.length} campaign-${r.weeks.length === 1 ? "week" : "weeks"}`];
  if (r.skippedCampaigns.length > 0) parts.push(`skipped ${r.skippedCampaigns.length} other ${r.skippedCampaigns.length === 1 ? "campaign" : "campaigns"}`);
  if (r.unreadable > 0) parts.push(`${r.unreadable} ${r.unreadable === 1 ? "row" : "rows"} could not be read`);
  return parts.join(", ");
}

/** A plain reason for each way LinkedIn can say no. */
export function linkedinErrorText(status: number): string {
  if (status === 401) return "LinkedIn did not accept the saved sign-in. Reconnect it in API keys";
  if (status === 403) return "LinkedIn says this app is not allowed to read that ad account. Check the account number and the app's access";
  if (status === 404) return "LinkedIn could not find that ad account. Check the account number";
  if (status === 429) return "LinkedIn asked us to wait. We will try again next week";
  return `LinkedIn answered with an error (${status})`;
}
